import { computed, readonly, shallowRef } from 'vue'
import { AppProblemModel } from '@/services/api/AppProblemModel'
import { resolveProblemMessage } from '@/services/api/resolveProblemMessage'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useApiClient } from '~/services/api'
import type { StepUpCredential } from '~/types/StepUpCredential'

/**
 * The pending confirmation is held module-side, not in `useState`: a Promise
 * resolver is not serialisable and this is only ever assigned on the client
 * (the API interceptor opens the prompt behind an `import.meta.client` guard).
 * Keeping it here lets concurrent gated requests that each hit a 423 share one
 * prompt instead of stacking a modal per request.
 */
let pendingResolve: ((confirmed: boolean) => void) | null = null
let pendingPromise: Promise<boolean> | null = null

const stepUpOpen = () => useState<boolean>('two-factor-step-up:open', () => false)
const stepUpSubmitting = () => useState<boolean>('two-factor-step-up:submitting', () => false)
const stepUpError = shallowRef<(() => string) | null>(null)

// Module-side so an identity change can settle a prompt no component is rendering.
const settle = (confirmed: boolean): void => {
  stepUpOpen().value = false
  stepUpSubmitting().value = false
  stepUpError.value = null
  const resolve = pendingResolve
  pendingResolve = null
  pendingPromise = null
  resolve?.(confirmed)
}

/**
 * Client-side two-factor "step-up" (APP-790 / APP-778).
 *
 * When a financial-routing or permission-changing request comes back with
 * `STEP_UP_REQUIRED` (HTTP 423), `useApiRequest` calls `requestStepUp()`, which
 * opens the mounted `<StepUpModal>`. The user re-verifies a code, the session is
 * stamped, and the original request is replayed. The composable owns only the
 * prompt lifecycle; the modal owns the input.
 */
export const useStepUp = () => {
  const isOpen = stepUpOpen()
  const submitting = stepUpSubmitting()
  const errorMessage = computed(() => stepUpError.value?.() ?? null)
  const t = usePresentationTranslator()

  /**
   * Open the prompt (or reuse the one already open) and resolve once the user
   * confirms a code (`true`) or dismisses it (`false`).
   */
  const requestStepUp = (): Promise<boolean> => {
    if (pendingPromise) return pendingPromise

    pendingPromise = new Promise<boolean>((resolve) => {
      pendingResolve = resolve
    })
    stepUpError.value = null
    submitting.value = false
    isOpen.value = true

    return pendingPromise
  }

  /**
   * Verify a code with the API. On success the session is stamped and every
   * queued request is released; on failure the prompt stays open with a message
   * so the user can retry a mistyped code.
   */
  const submit = async (credential: StepUpCredential): Promise<void> => {
    if (submitting.value) return

    submitting.value = true
    stepUpError.value = null

    try {
      await useApiClient().settings().confirmStepUp(credential)
      settle(true)
    } catch (error) {
      submitting.value = false
      const problem = AppProblemModel.normalize(error, { context: 'two-factor step-up' })
      stepUpError.value = () => resolveProblemMessage(problem, t)
    }
  }

  /** Dismiss the prompt; the pending gated request is abandoned with its 423. */
  const cancel = (): void => {
    settle(false)
  }

  return {
    isOpen: readonly(isOpen),
    submitting: readonly(submitting),
    errorMessage: readonly(errorMessage),
    requestStepUp,
    submit,
    cancel
  }
}
